import React from 'react'
import {
    Avatar,
    Button,
    Divider,
    Drawer,
    IconButton,
    Switch,
    Typography,
    makeStyles
} from '@material-ui/core'
import {
    Close as CloseIcon,
    VpnKeyOutlined as KeyIcon,
    DeleteOutlined as DeleteIcon,
    EditOutlined as EditIcon,
    SwapHorizOutlined as MoveIcon
} from '@material-ui/icons'

import {
    useSetAdminMutation,
    useRemoveAdminMutation,
    useSetSuperAdminMutation,
    useRemoveSuperAdminMutation,
    useEnableUserMutation,
    useDeleteUserMutation,
    forcePasswordReset
} from 'components/api'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'
import { dateFormatter } from 'components'
import { EditUserModal } from 'components/admin'
import ChangeTenantDialog from './ChangeTenantDialog'

const useStyles = makeStyles({
    paper: {
        width: 440,
        maxWidth: '92vw'
    }
})

function isTrue(value) {
    return String(value).toLowerCase() === 'true'
}

function initials(user) {
    const g = (user.given_name || '')[0] || ''
    const f = (user.family_name || '')[0] || ''
    return (g + f).toUpperCase() || (user.email || '?')[0].toUpperCase()
}

function StatusBadge({ user }) {
    let cls = 'status-active'
    let label = 'Active'
    if (!user.Enabled) {
        cls = 'status-disabled'
        label = 'Disabled'
    } else if (user.UserStatus === 'FORCE_CHANGE_PASSWORD' || user.UserStatus === 'RESET_REQUIRED') {
        cls = 'status-reset'
        label = 'Reset required'
    }
    return (
        <span className={`superadmin-status-badge ${cls}`}>
            <span className="dot" />
            {label}
        </span>
    )
}

export default function UserManagementDrawer({
    user,
    open,
    onClose,
    onChanged,
    onToast,
    onError,
    confirmRef
}) {
    const classes = useStyles()
    const editRef = React.useRef()
    const changeTenantRef = React.useRef()

    const { mutateAsync: setAdmin } = useSetAdminMutation()
    const { mutateAsync: removeAdmin } = useRemoveAdminMutation()
    const { mutateAsync: setSuperAdmin } = useSetSuperAdminMutation()
    const { mutateAsync: removeSuperAdmin } = useRemoveSuperAdminMutation()
    const { mutateAsync: enableUser } = useEnableUserMutation()
    const { mutateAsync: deleteUser } = useDeleteUserMutation()

    const [busy, setBusy] = React.useState(false)

    const run = React.useCallback(async (action, successMsg, fallback) => {
        try {
            setBusy(true)
            await action()
            if (successMsg && onToast) onToast(successMsg)
            if (onChanged) await onChanged()
        } catch (err) {
            if (onError) onError(getApiErrorMessage(err, fallback))
        } finally {
            setBusy(false)
        }
    }, [onChanged, onToast, onError])

    if (!user) {
        return <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.paper }} />
    }

    const email = user.email
    const admin = isTrue(user['custom:is_admin'])
    const superAdmin = isTrue(user['custom:is_super_admin'])

    const onToggleAdmin = e => {
        const grant = e.target.checked
        run(
            () => (grant ? setAdmin({ email }) : removeAdmin({ email })),
            grant ? 'Admin role granted' : 'Admin role revoked',
            'Failed to update admin role'
        )
    }

    const onToggleSuperAdmin = e => {
        const grant = e.target.checked
        if (grant) {
            confirmRef.current.confirm({
                title: 'Grant Super Admin?',
                message: `Give ${user.given_name} ${user.family_name} cross-tenant super admin access?`,
                confirmLabel: 'Grant Super Admin',
                onConfirm: () => run(
                    () => setSuperAdmin({ email }),
                    'Super admin role granted',
                    'Failed to grant super admin'
                )
            })
        } else {
            run(
                () => removeSuperAdmin({ email }),
                'Super admin role revoked',
                'Failed to revoke super admin'
            )
        }
    }

    const onForceReset = () => {
        if (user.UserStatus === 'FORCE_CHANGE_PASSWORD') {
            if (onError) onError('This user has never signed in, so a reset email cannot be sent. Re-invite them instead.')
            return
        }
        confirmRef.current.confirm({
            title: 'Force password reset?',
            message: `Send a password reset email to ${email}? They will receive a code to set a new password.`,
            confirmLabel: 'Send reset email',
            onConfirm: () => run(
                () => forcePasswordReset(email),
                `Reset email sent to ${email}`,
                'Failed to send reset email'
            )
        })
    }

    const onToggleEnabled = () => {
        if (user.Enabled) {
            confirmRef.current.confirm({
                title: 'Disable user?',
                message: `${user.given_name} ${user.family_name} will no longer be able to sign in.`,
                confirmLabel: 'Disable user',
                destructive: true,
                onConfirm: () => run(
                    () => enableUser({ email, enabled: true }),
                    'User disabled',
                    'Failed to disable user'
                )
            })
        } else {
            run(
                () => enableUser({ email, enabled: false }),
                'User enabled',
                'Failed to enable user'
            )
        }
    }

    const onDelete = () => {
        confirmRef.current.confirm({
            title: 'Delete user?',
            message: `Permanently delete ${user.given_name} ${user.family_name}? This cannot be undone.`,
            confirmLabel: 'Delete user',
            destructive: true,
            onConfirm: () => run(
                async () => {
                    await deleteUser({ email })
                    onClose()
                },
                'User deleted',
                'Failed to delete user'
            )
        })
    }

    return (
        <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.paper }}>
            <div className="superadmin-user-drawer">
                <div className="drawer-header">
                    <Avatar className={`drawer-avatar ${user.Enabled ? '' : 'disabled'}`}>
                        {initials(user)}
                    </Avatar>
                    <div className="who">
                        <Typography variant="subtitle1" className="name">
                            {user.family_name}, {user.given_name}
                        </Typography>
                        <Typography variant="body2" className="email">{email}</Typography>
                    </div>
                    <IconButton size="small" onClick={onClose} aria-label="Close">
                        <CloseIcon />
                    </IconButton>
                </div>

                <Divider />

                <div className="drawer-body">
                    <section className="drawer-section">
                        <h4>Identity</h4>
                        <div className="kv"><span className="k">Tenant</span><span>{user.tenant_name || user['custom:tenantid'] || '—'}</span></div>
                        <div className="kv"><span className="k">Status</span><StatusBadge user={user} /></div>
                        <div className="kv"><span className="k">Created</span><span>{dateFormatter(user.UserCreateDate)}</span></div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<EditIcon />}
                                className="drawer-outline-btn"
                                onClick={() => editRef.current.open(user)}
                                disabled={busy}
                            >
                                Edit name
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<MoveIcon />}
                                className="drawer-outline-btn"
                                onClick={() => changeTenantRef.current.open(user)}
                                disabled={busy}
                            >
                                Change tenant
                            </Button>
                        </div>
                    </section>

                    <section className="drawer-section">
                        <h4>Roles</h4>
                        <div className="toggle-row">
                            <div>
                                <div className="label">Admin</div>
                                <div className="desc">Manage their own tenant's users</div>
                            </div>
                            <Switch color="primary" checked={admin} onChange={onToggleAdmin} disabled={busy} />
                        </div>
                        <div className="toggle-row">
                            <div>
                                <div className="label">Super Admin</div>
                                <div className="desc">Cross-tenant administration</div>
                            </div>
                            <Switch color="primary" checked={superAdmin} onChange={onToggleSuperAdmin} disabled={busy} />
                        </div>
                    </section>

                    <section className="drawer-section">
                        <h4>Security</h4>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<KeyIcon />}
                            onClick={onForceReset}
                            disabled={busy}
                        >
                            Force password reset
                        </Button>
                        <Typography variant="caption" display="block" className="section-hint">
                            Sends a Cognito password reset email to the user.
                        </Typography>
                    </section>

                    <section className="drawer-section">
                        <h4>Account status</h4>
                        <Button
                            variant={user.Enabled ? 'outlined' : 'contained'}
                            color={user.Enabled ? 'default' : 'primary'}
                            className={user.Enabled ? 'drawer-outline-btn' : undefined}
                            onClick={onToggleEnabled}
                            disabled={busy}
                        >
                            {user.Enabled ? 'Disable user' : 'Enable user'}
                        </Button>
                    </section>

                    <section className="drawer-section">
                        <div className="danger-zone">
                            <h4>Danger zone</h4>
                            <Typography variant="caption" display="block" className="section-hint">
                                {user.Enabled
                                    ? 'User must be disabled before deletion.'
                                    : 'This permanently deletes the user.'}
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<DeleteIcon />}
                                className="danger-btn"
                                onClick={onDelete}
                                disabled={busy || user.Enabled}
                                style={{ marginTop: 8 }}
                            >
                                Delete user
                            </Button>
                        </div>
                    </section>
                </div>
            </div>
            <EditUserModal ref={editRef} onSaved={onChanged} />
            <ChangeTenantDialog
                ref={changeTenantRef}
                confirmRef={confirmRef}
                onChanged={onChanged}
                onToast={onToast}
                onError={onError}
            />
        </Drawer>
    )
}
